import PopupTemplate from '@arcgis/core/PopupTemplate';
import type { ResourceSet } from 'components/IAP/hooks/resourceSetHooks';
import type { CfaDemoStore } from 'components/MapPage/CorporateViewTabs/LayersTab/cfa-demo/historicalHurricanesHeleneData';
import {
  USCG_SYMBOLOGY_OPTIONS,
  type UscgSymbol,
} from 'components/MapPage/CorporateViewTabs/LayersTab/utils/uscgSymbology';
import { NULL_VALUE } from 'constants/displayConstants';
import { CFA_HELENE_SYMBOLOGY } from './useEnsureHeleneConeResourceSets';

function display(value: unknown): string {
  if (value == null || value === '') return NULL_VALUE;
  return String(value);
}

function geometryCoords(geometry: ResourceSet['geometry']): {
  lat?: number;
  lon?: number;
} {
  if (
    !geometry ||
    typeof geometry !== 'object' ||
    !Array.isArray(geometry.coordinates)
  ) {
    return {};
  }
  const [lon, lat] = geometry.coordinates;
  return {
    lon: lon != null ? Number(lon) : undefined,
    lat: lat != null ? Number(lat) : undefined,
  };
}

export function buildHeleneResourcePopupAttributes({
  resource,
  store,
  aorNames,
}: {
  resource: ResourceSet;
  store?: CfaDemoStore;
  aorNames?: { opcon?: string; tacon?: string };
}): Record<string, string | number | boolean | null | undefined> {
  const coords = geometryCoords(resource.geometry);
  const lat = coords.lat ?? store?.lat;
  const lon = coords.lon ?? store?.lon;
  const symbologyType =
    (resource.symbology as UscgSymbol | undefined) || CFA_HELENE_SYMBOLOGY;
  const symbologyLabel =
    USCG_SYMBOLOGY_OPTIONS.find((opt) => opt.type === symbologyType)?.label ||
    symbologyType;

  return {
    id: store?.id,
    name: resource.name,
    assetStatus: display(resource.asset_status),
    address: display(resource.address),
    opcon: display(aorNames?.opcon || resource.opcon),
    tacon: display(aorNames?.tacon || resource.tacon),
    pointOfContact: display(resource.point_of_contact),
    owningOrganization: display(resource.owning_organization),
    quantity: resource.quantity ?? NULL_VALUE,
    unit: display(resource.unit),
    hullTailNumber: display(resource.hull_tail_number),
    symbology: symbologyLabel,
    latitude: lat != null ? Math.round(lat * 100) / 100 : NULL_VALUE,
    longitude: lon != null ? Math.round(lon * 100) / 100 : NULL_VALUE,
    capabilities: display(resource.description),
    storeNumber: store?.storeNumber,
    city: store?.city,
    state: store?.state,
    inConeLabel: store ? (store.inCone ? 'Yes' : 'No') : NULL_VALUE,
    kind: display(resource.kind),
    type: display(resource.type),
    status: display(resource.current_status || resource.status),
  };
}

/** Popup showing Helene cone resource fields (fields only; no media). */
export function buildHeleneResourcePopupTemplate(): PopupTemplate {
  return new PopupTemplate({
    title: '{name}',
    content: [
      {
        type: 'fields',
        fieldInfos: [
          { fieldName: 'symbology', label: 'Symbology' },
          { fieldName: 'assetStatus', label: 'Asset Status' },
          { fieldName: 'status', label: 'Status' },
          { fieldName: 'kind', label: 'Kind' },
          { fieldName: 'type', label: 'Type' },
          { fieldName: 'address', label: 'Current Location' },
          { fieldName: 'opcon', label: 'OPCON' },
          { fieldName: 'tacon', label: 'TACON' },
          { fieldName: 'pointOfContact', label: 'Point of Contact' },
          { fieldName: 'owningOrganization', label: 'Owning Organization' },
          { fieldName: 'quantity', label: 'Quantity' },
          { fieldName: 'unit', label: 'Unit' },
          { fieldName: 'hullTailNumber', label: 'Hull/Tail Number' },
          { fieldName: 'latitude', label: 'Lat' },
          { fieldName: 'longitude', label: 'Long' },
          { fieldName: 'capabilities', label: 'Capabilities' },
          { fieldName: 'storeNumber', label: 'Store #' },
          { fieldName: 'city', label: 'City' },
          { fieldName: 'state', label: 'State' },
          { fieldName: 'inConeLabel', label: 'In forecast cone' },
        ],
      },
    ],
  });
}

export function applyHeleneResourcePopupToGraphic({
  graphic,
  resource,
  store,
  aorNames,
}: {
  graphic: __esri.Graphic;
  resource: ResourceSet;
  store?: CfaDemoStore;
  aorNames?: { opcon?: string; tacon?: string };
}): void {
  graphic.attributes = {
    ...(graphic.attributes ?? {}),
    ...buildHeleneResourcePopupAttributes({ resource, store, aorNames }),
  };
  graphic.popupTemplate = buildHeleneResourcePopupTemplate();
}
